import React, { useState, useEffect } from 'react';
import '../css/Modal.css';
import Translate, { t } from "./Translate";
import { getCurrentUser } from "../services/authService";
import { toast } from "react-toastify";

const currentUser = getCurrentUser() ? getCurrentUser() : { language: 'en' };

const ExportModal = ({ isOpen, onClose, project, apiURL, locales }) => {
    const [betaMode, setBetaMode] = useState(false);
    const [fromCache, setFromCache] = useState(true);
    const [selectedLocale, setSelectedLocale] = useState('');
    const [keyPrefix, setKeyPrefix] = useState('');
    const [exportResult, setExportResult] = useState('');
    const [fetchTime, setFetchTime] = useState(null);

    useEffect(() => {
        if (betaMode || selectedLocale || keyPrefix) {
            setFromCache(false);
        }
    }, [betaMode, selectedLocale, keyPrefix]);

    if (!isOpen) return null;

    const buildQueryParams = () => {
        const params = new URLSearchParams();
        if (betaMode) params.append('betaMode', betaMode);
        if (!fromCache) {
            params.append('fromDB', !fromCache);
        }
        if (selectedLocale) params.append('locale', selectedLocale);
        if (keyPrefix) params.append('keyPrefix', keyPrefix);
        return params.toString();
    };

    const exportURL = `${apiURL}/resource-provider/api/resources/${project.name}?${buildQueryParams()}`;

    const handleExport = async () => {
        const startTime = performance.now();
        try {
            const response = await fetch(exportURL);
            const endTime = performance.now();
            setFetchTime(Math.round(endTime - startTime));
            if (response.ok) {
                const result = await response.text();
                try {
                    const jsonResult = JSON.parse(result);
                    setExportResult(JSON.stringify(jsonResult, null, 2));
                } catch (error) {
                    setExportResult(result);
                }
            } else {
                setExportResult('Error fetching export data.');
            }
        } catch (error) {
            setExportResult('Error fetching export data.');
            setFetchTime(null);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(exportResult);
        toast.info(t(currentUser.language, 'copied'), {
            position: "top-right",
            theme: "light",
            autoClose: 500,
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3><Translate language={currentUser.language} text={'configureExportText'} /></h3>

                <div className="modal-options">
                    <div className="option-item">
                        <label>
                            <Translate language={currentUser.language} text={'betaMode'} />:
                        </label>
                        <input
                            type="checkbox"
                            checked={betaMode}
                            onChange={() => setBetaMode(!betaMode)}
                        />
                    </div>

                    <div className="option-item">
                        <label>
                            <Translate language={currentUser.language} text={'fromCache'} />:
                        </label>
                        <input
                            type="checkbox"
                            checked={fromCache}
                            onChange={() => setFromCache(!fromCache)}
                            disabled={betaMode || selectedLocale || keyPrefix}
                        />
                    </div>

                    <div className="option-item">
                        <label>
                            <Translate language={currentUser.language} text={'chooseLocale'} />:
                        </label>
                        <div className="modal-locale-options">
                            {locales.map(locale => (
                                <div
                                    key={locale.locale}
                                    className={`modal-locale-circle ${selectedLocale === locale.locale ? 'selected' : ''}`}
                                    onClick={() => setSelectedLocale(selectedLocale === locale.locale ? '' : locale.locale)}
                                >
                                    {locale.locale}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="option-item">
                        <label>
                            <Translate language={currentUser.language} text={'keyPrefix'} />:
                        </label>
                        <input
                            type="text"
                            style={{ width: '93%' }}
                            className={'resources-search-input'}
                            value={keyPrefix}
                            onChange={(e) => setKeyPrefix(e.target.value)}
                        />
                    </div>
                </div>

                <div className="modal-buttons">
                    <button
                        className="modal-button cancel"
                        onClick={() => {
                            onClose();
                            setBetaMode(false);
                            setFromCache(true);
                            setKeyPrefix("");
                            setSelectedLocale("");
                            setExportResult('');
                            setFetchTime(null);
                        }}
                    >
                        <Translate language={currentUser.language} text={'close'} />
                    </button>
                    <a
                        href={exportURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="modal-button confirm a-tag"
                    >
                        <Translate language={currentUser.language} text={'exportInNewTab'} />
                    </a>
                    <button
                        className="modal-button confirm"
                        onClick={handleExport}
                    >
                        <Translate language={currentUser.language} text={'export'} />
                    </button>
                </div>

                {exportResult && (
                    <div className="export-result">
                        <textarea
                            value={exportResult}
                            readOnly
                            className="export-textarea"
                        />
                        <div className="export-meta">
                            <button className="modal-button copy" onClick={handleCopy}>
                                <Translate language={currentUser.language} text={'copy'} />
                            </button>
                            {fetchTime !== null && (
                                <span className="fetch-time">
                                    <Translate language={currentUser.language} text={'fetchTime'} />:&nbsp;
                                    {fetchTime}
                                    <Translate language={currentUser.language} text={'ms'} />
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExportModal;
